import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import OkCancelModal from '@/shared/components/OkCancelModal';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import Checkbox from '@/shared/components/input/Checkbox';
import api from '@/shared/api';
import config from '@/config';
import moment from 'moment';
import classNames from 'classnames';
import intlShape from '@/shared/utils/intlShape';
import DatePicker from '@/shared/components/input/DatePicker';
import DataCacheService from '@/shared/services/DataCacheService';
import CanRead from '@/shared/components/security/CanRead';
import UserSearch from '@/shared/components/input/UserSearch';
import YearSelect from '@/shared/components/input/YearSelect';
import ResourceAutoComplete from './ResourceAutoComplete';
import messages from './registrationMessages';
import { retrieveResources } from './resourceApi';
import { isBeforeMinimum } from '../../utils/validationHelpers';
import AuthService from '@/shared/services/AuthService';
import { guid1 } from '@/shared/utils/guid';
import TimeButtons from './partials/TimeButtons';
import { sanitizeReferenceYear } from './registrationHelpers';
import { CanCreate } from '../security';

class BatchRegistrationModal extends Component {
  state = {};

  static propTypes = {
    onSave: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    show: PropTypes.bool,
    defaultResourceId: PropTypes.string,
    countSelectedRelations: PropTypes.number,
    calculateDurationPerRelation: PropTypes.bool,
    assignmentTemplateId: PropTypes.string,
    crmType: PropTypes.number,
    referenceYear: PropTypes.number,
  };

  static defaultProps = {
    show: false,
    defaultResourceId: undefined,
    countSelectedRelations: 0,
    calculateDurationPerRelation: true,
    assignmentTemplateId: undefined,
    crmType: undefined,
    referenceYear: undefined,
  };

  constructor(props) {
    super(props);
    this.state = {
      selectedTask: undefined,
      durationPerRelationActive: true,
      showValidation: false,
      registration: {
        type: 0,
        date: moment.utc().startOf('day'),

        durationHour: 0,
        durationMinutes: 0,
        durationMinutesTotal: 0,

        fromHour: 0,
        fromMinutes: 0,
        fromMinutesTotal: 0,

        quantity: 0,
        isBillable: true,
        resource: undefined,
        referenceYear: sanitizeReferenceYear(props.referenceYear),
      },
    };
  }

  componentDidMount() {
    const cache = DataCacheService.getDataCache();

    if (!cache) {
      throw new Error('DataCache not found in RegistrationModal.jsx');
    }

    this.form = React.createRef();

    this.retrieveResources(0);

    let employeeStartHour = cache.employee && cache.employee.workDayStartHour;
    let employeeEndHour = cache.employee && cache.employee.workDayEndHour;

    if (employeeStartHour) {
      employeeStartHour = moment(employeeStartHour, 'HH:mm:ss');
    }
    if (employeeEndHour) {
      employeeEndHour = moment(employeeEndHour, 'HH:mm:ss');
    }

    const { registration } = this.state;

    this.getLastRegistrationTime(registration.date);

    this.setState(state => ({
      ...state,
      employeeStartHour,
      employeeEndHour,
    }));
  }

  onResourceTypeChanged = resourceType => {
    this.setState(
      state => ({
        ...state,
        registration: {
          ...state.registration,
          type: parseInt(resourceType),
          quantity: parseInt(resourceType) === 1 ? 1 : 0,
          resource: undefined,
        },
      }),
      () => {
        this.retrieveResources(resourceType);
      },
    );
  };

  retrieveResources = resourceType => {
    const { intl, defaultResourceId } = this.props;
    retrieveResources(intl, resourceType).then(resources => {
      const defaultResource = defaultResourceId
        ? resources.find(x => x.id === defaultResourceId)
        : null;
      this.setState(
        state => ({
          ...state,
          resources,
          registration: defaultResource
            ? {
                ...state.registration,
                isBillable: defaultResource.isBillable,
                resource: defaultResource,
              }
            : state.registration,
        }),
        () => {
          const { registration } = this.state;
          if (registration.resource) {
            this.onResourceChanged(registration.resource);
          }
        },
      );
    });
  };

  getLastRegistrationTime = date => {
    const now = new Date();
    let hour = now.getHours();
    let minutes = now.getMinutes();

    api.base
      .get(
        `${config.appUrls.api}/api/registration/LastRegistrationTime?date=${moment(date).format(
          'YYYY-MM-DD',
        )}`,
      )
      .then(
        ({ data }) => {
          if (data) {
            // TimeSpan was sent back from back-end, parse it to moment obj and retrieve hour & minutes
            const momentDate = moment(data, 'HH:mm:ss');

            hour = momentDate.hour();
            minutes = momentDate.minutes();
          }

          const hourInMinutes = hour > 0 ? hour * 60 : 0;
          const totalMinutes = hourInMinutes + minutes;

          this.fromTotalMinutesChanged(totalMinutes);
        },
        error => console.error(error),
      );
  };

  onResourceChanged = resource => {
    if (resource === undefined) return;
    const { registration } = this.state;

    this.setState({
      registration: {
        ...registration,
        isBillable: resource ? resource.isBillable : true,
        resource,
      },
    });
  };

  userChanged = user => {
    this.setState(({ registration }) => ({ registration: { ...registration, userId: user?.id } }));
  };

  convertHoursToMinutes = hours => (hours > 0 ? hours * 60 : 0);

  getTotalDurationMinutes = (toMinutesTotal, fromMinutesTotal) => toMinutesTotal - fromMinutesTotal;

  fromHourChanged = hour => {
    const { registration } = this.state;
    const { fromMinutes } = registration;

    const hourInMinutes = this.convertHoursToMinutes(hour);
    const fromMinutesTotal = hourInMinutes + fromMinutes;

    this.fromTotalMinutesChanged(fromMinutesTotal);
  };

  fromMinutesInputChanged = minutes => {
    const { registration } = this.state;
    const { fromMinutes, fromMinutesTotal } = registration;

    const newFromMinutesTotal = minutes - fromMinutes + fromMinutesTotal;

    this.fromTotalMinutesChanged(newFromMinutesTotal);
  };

  fromTotalMinutesChanged = minutes => {
    const { registration } = this.state;

    // Max 23h59 or 1439 minutes
    if (minutes > 1439) {
      minutes = 1439;
    }

    // If the minutes > 60: calculate the amount of hours that were entered
    const durationHour = parseInt(minutes / 60);

    // Subtract the hours from the minutes
    const fromMinutes = parseInt(minutes - durationHour * 60);

    // Add the new amount of hours entered to the current amount of hours
    const fromHour = parseInt(durationHour);

    // Calculate the total amount of minutes
    const fromMinutesTotal = fromHour * 60 + fromMinutes;

    this.setState({
      registration: {
        ...registration,
        fromHour,
        fromMinutes,
        fromMinutesTotal,
      },
    });
  };

  durationMinutesChanged = minutes => {
    const { registration } = this.state;

    // If the minutes > 60: calculate the amount of hours that were entered
    const durationHour = parseInt(minutes / 60);

    // Subtract the hours from the minutes
    const durationMinutes = parseInt(minutes - durationHour * 60);

    // Add the new amount of hours entered to the current amount of hours
    const newHours = parseInt(durationHour);

    // Calculate the total amount of minutes
    const durationMinutesTotal = newHours * 60 + durationMinutes;

    this.setState({
      registration: {
        ...registration,
        durationHour: newHours,
        durationMinutes,
        durationMinutesTotal,
      },
    });
  };

  durationMinutesInputChanged = minutes => {
    this.setState({ touchedDuration: true });

    if (minutes < 0) {
      minutes = 0;
    }

    const { registration } = this.state;
    const { durationMinutesTotal, durationMinutes } = registration;

    const newDurationMinutesTotal = durationMinutesTotal + (minutes - durationMinutes);

    this.durationMinutesChanged(newDurationMinutesTotal);
  };

  durationHourInputChanged = hour => {
    this.setState({ touchedDuration: true });

    if (hour < 0) {
      hour = 0;
    }

    const { registration } = this.state;
    const { durationMinutes } = registration;

    const hourInMinutes = hour > 0 ? hour * 60 : 0;
    const newDurationMinutesTotal = hourInMinutes + durationMinutes;

    this.durationMinutesChanged(newDurationMinutesTotal);
  };
  isFormInvalid = () => {
    const { registration } = this.state;
    const { calculateDurationPerRelation } = this.props;
    if (!registration.date) return true;
    if (!registration.resource) return true;
    if (!registration.resource.id) return true;

    if (registration.type === 0) {
      if (registration.durationMinutesTotal <= 0) return true;
      if (calculateDurationPerRelation && this.calculateDurationPerRelation() <= 0) return true;
    }
    if (registration.type === 1 && registration.quantity < 1) return true;

    return false;
  };
  saveRegistration = () => {
    const { onSave, countSelectedRelations, calculateDurationPerRelation } = this.props;

    const { registration, selectedTask, durationPerRelationActive } = this.state;
    if (this.isFormInvalid()) return;

    const model = { ...registration };

    const duration = calculateDurationPerRelation
      ? durationPerRelationActive
        ? model.durationMinutesTotal * countSelectedRelations
        : Math.round(this.calculateDurationPerRelation() * countSelectedRelations)
      : model.durationMinutesTotal;

    model.durationMinutesTotal = duration;
    model.subtaskTemplateId = selectedTask ? selectedTask.id : null;
    model.fromMinutes = model.fromMinutesTotal;

    return onSave(model);
  };

  closeModal = () => {
    const { onClose } = this.props;
    onClose();
  };

  addMinutes = minutes => {
    const { registration } = this.state;
    const totalDurationMinutes = registration.durationMinutesTotal + minutes;

    this.durationMinutesChanged(totalDurationMinutes);
  };

  dateChanged = date => {
    if (date) {
      this.getLastRegistrationTime(date);
    }

    this.setState(({ registration }) => ({
      registration: {
        ...registration,
        date,
      },
    }));
  };

  referenceYearChanged = value => {
    this.setState(({ registration }) => ({
      registration: {
        ...registration,
        referenceYear: sanitizeReferenceYear(parseInt(value), registration.date),
      },
    }));
  };

  quantityChanged = quantity => {
    const { registration } = this.state;

    if (quantity < 1) {
      quantity = 1;
    }

    this.setState({
      registration: {
        ...registration,
        quantity,
      },
    });
  };

  taskChanged = taskId => {
    const { tasks } = this.props;
    const { resources } = this.state;
    const task = tasks.find(x => x.id === taskId);
    this.setState({
      selectedTask: task,
    });

    if (task.resourceId) {
      const resource = resources.find(x => x.id === task.resourceId);
      if (resource != null) {
        this.onResourceChanged(resource);
      }
    }
  };

  calculateDurationPerRelation = () => {
    const { registration, durationPerRelationActive } = this.state;
    const { countSelectedRelations } = this.props;
    const totalDuration = registration.durationMinutesTotal;

    if (countSelectedRelations === 0) return 0; // wie deelt door 0 is een snul
    if (durationPerRelationActive) {
      return totalDuration;
    }
    const durationPerRelation = totalDuration / countSelectedRelations;

    // round to nearest int
    return Math.floor(durationPerRelation);
  };

  showValidation = () => this.setState({ showValidation: true });

  render() {
    const { show, intl, tasks, countSelectedRelations, crmType, assignmentTemplateId } = this.props;
    const {
      dateFocused,
      resources,
      registration,
      touchedDuration,
      selectedTask,
      employeeStartHour,
      employeeEndHour,
      durationPerRelationActive,
      showValidation,
    } = this.state;
    const invalidResource = !registration.resource;

    const durationClassName = classNames('form-group', {
      'has-error': (showValidation || touchedDuration) && registration.durationMinutesTotal <= 0,
    });
    const durationPerRelation = registration.type !== 0 ? 0 : this.calculateDurationPerRelation();

    let surpassesCurrentDay = false;
    const isSystemUser = registration.userId && registration.userId === guid1;

    // if the registration surpasses the current day, the next registration will be made on the next day, starting at the employee's starting workhour
    if (
      registration.date &&
      registration.type === 0 &&
      employeeStartHour &&
      employeeEndHour &&
      !isSystemUser
    ) {
      const startTime = moment().set({
        year: registration.date.year(),
        month: registration.date.month(),
        day: registration.date.date(),
        hour: registration.fromHour,
        minute: registration.fromMinutes,
      });

      const totalMinutes = durationPerRelationActive
        ? registration.durationMinutesTotal * countSelectedRelations
        : Math.round(durationPerRelation * countSelectedRelations);

      const endTime = startTime.clone().add(totalMinutes, 'minutes');

      if (endTime.isAfter(startTime, 'day')) {
        surpassesCurrentDay = true;
      }
    }

    return (
      <OkCancelModal
        title={intl.formatMessage(messages.Registration)}
        show={show}
        onClose={this.closeModal}
        disabled={showValidation && this.isFormInvalid()}
        onSave={this.saveRegistration}
        onHoverSave={this.showValidation}
      >
        {show && (
          <form ref={this.form}>
            <div className="form-horizontal">
              <div
                className={classNames('form-group', {
                  'has-error': !registration.date,
                })}
              >
                <label className="control-label col-sm-2">
                  {intl.formatMessage(messages.Date)}
                </label>
                <div className="col-sm-4">
                  <DatePicker
                    placeholder={intl.formatMessage(messages.Date)}
                    date={registration.date}
                    onDateChange={this.dateChanged}
                    focused={dateFocused}
                    onFocusChange={({ focused }) => this.setState({ dateFocused: focused })}
                    id="registrationDate"
                    required
                    block
                    small
                    isOutsideRange={isBeforeMinimum}
                  />
                </div>
                {AuthService.hasFeature('ReferenceYear') ? (
                  <>
                    <label className="control-label col-sm-2">
                      {intl.formatMessage(messages.ReferenceYear)}
                    </label>
                    <div className="col-sm-4">
                      <YearSelect
                        id="referenceYearField"
                        value={registration.referenceYear}
                        onChange={this.referenceYearChanged}
                        className="form-control input-sm"
                        required
                      />
                    </div>
                  </>
                ) : null}
              </div>
              {tasks && (
                <div className="form-group">
                  <label className="control-label col-sm-2">
                    {intl.formatMessage(messages.SubTask)}
                  </label>
                  <div className="col-md-5">
                    <select
                      onChange={e => this.taskChanged(e.target.value)}
                      className="form-control form-field"
                      value={(selectedTask && selectedTask.id) || ''}
                    >
                      {!selectedTask && (
                        <option value="">{intl.formatMessage(messages.SelectASubtask)}</option>
                      )}
                      {tasks.map(task => (
                        <option key={task.id} value={task.id}>
                          {intl.formatMessage(listItemMsg('taskTemplate', task.id, task.name))}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              <div className="form-group">
                <div className="col-sm-offset-2 col-sm-5">
                  <label>
                    <input
                      id="time"
                      type="radio"
                      name="resourcetype"
                      value={0}
                      onChange={e => this.onResourceTypeChanged(e.target.value)}
                      defaultChecked
                    />
                    <label htmlFor="time" className="margin5L">
                      <FormattedMessage {...messages.Time} />
                    </label>
                  </label>

                  <label className="margin10L">
                    <input
                      id="cost"
                      type="radio"
                      name="resourcetype"
                      value={1}
                      onChange={e => this.onResourceTypeChanged(e.target.value)}
                    />
                    <label htmlFor="cost" className="margin5L">
                      <FormattedMessage {...messages.Cost} />
                    </label>
                  </label>
                </div>
              </div>
              <CanCreate on="RegistrationOthers">
                <div className="form-group">
                  <label className="control-label col-sm-2" htmlFor="user">
                    <FormattedMessage {...messages.User} />
                  </label>
                  <div className="col-sm-8">
                    <UserSearch
                      onChange={this.userChanged}
                      value={registration.userId}
                      withSystemUser
                    />
                  </div>
                </div>
              </CanCreate>
              <div className="form-group">
                <label className="control-label col-sm-2">
                  <FormattedMessage {...messages.What} />
                </label>
                <div className="col-sm-8">
                  {resources && resources.length && (
                    <ResourceAutoComplete
                      items={resources}
                      autoFocus
                      placeholder={intl.formatMessage(messages.SearchPriceListItem)}
                      onChange={this.onResourceChanged}
                      emptyLabel={intl.formatMessage(messages.NoPriceListItemsFound)}
                      value={registration.resource}
                      required
                      assignmentTemplateId={assignmentTemplateId}
                      crmType={crmType}
                      intl={intl}
                    />
                  )}
                </div>
              </div>
              {registration.type === 0 && (
                <div>
                  <div className="form-group">
                    {!isSystemUser && (
                      <>
                        <label className="control-label col-sm-2">
                          <FormattedMessage {...messages.From} />
                        </label>
                        <div className="col-sm-4">
                          <div className="input-group-sm time-input input-group">
                            <input
                              id="FromHourField"
                              type="number"
                              step="1"
                              disabled={invalidResource}
                              value={registration.fromHour}
                              onChange={e => this.fromHourChanged(e.target.value)}
                              className="form-control"
                            />
                            <span className="input-group-addon">:</span>
                            <input
                              id="FromMinuteField"
                              type="number"
                              step="1"
                              disabled={invalidResource}
                              value={registration.fromMinutes}
                              onChange={e => this.fromMinutesInputChanged(e.target.value)}
                              className="form-control"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className={durationClassName}>
                    <label className="control-label col-sm-2">
                      <FormattedMessage {...messages.Duration} />
                    </label>
                    <div className="col-sm-4">
                      <div className="input-group-sm time-input input-group">
                        <input
                          id="HourField"
                          type="number"
                          step="1"
                          disabled={invalidResource}
                          value={registration.durationHour}
                          onChange={e => this.durationHourInputChanged(e.target.value)}
                          className="form-control "
                        />
                        <span className="input-group-addon">:</span>
                        <input
                          id="MinuteField"
                          type="number"
                          step="1"
                          disabled={invalidResource}
                          value={registration.durationMinutes}
                          onChange={e => this.durationMinutesInputChanged(e.target.value)}
                          className="form-control"
                        />
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <TimeButtons
                        addMinutes={[5, 10, 15, 30, 60]}
                        roundMinutes={[]}
                        value={registration.durationMinutesTotal}
                        onChange={this.durationMinutesChanged}
                        disabled={invalidResource}
                        intl={intl}
                      />
                    </div>
                  </div>

                  {countSelectedRelations > 1 && durationPerRelation > 0 && (
                    <div className="form-group">
                      <div className="col-sm-10 col-sm-offset-2">
                        <FormattedMessage
                          {...messages.RegistrationDurationPerRelation}
                          values={{
                            minutes: durationPerRelation,
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {countSelectedRelations > 1 &&
                    registration.durationMinutesTotal > 0 &&
                    durationPerRelation === 0 && (
                      <div className="form-group">
                        <div className="col-sm-10 col-sm-offset-2 ">
                          <div
                            className="alert alert-danger"
                            role="alert"
                            style={{ padding: '2px 10px' }}
                          >
                            <FormattedMessage
                              {...messages.DurationPerRelationMinimum1MinuteWarning}
                              values={{
                                minutes: countSelectedRelations.minuteToTime(),
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                  {surpassesCurrentDay && (
                    <div className="form-group">
                      <div className="col-sm-8 col-sm-offset-2">
                        <div
                          className=" alert alert-danger"
                          role="alert"
                          style={{ padding: '2px 10px' }}
                        >
                          <FormattedMessage
                            {...messages.RegistrationOutsideWorkingHoursWarning}
                            values={{
                              startHour: employeeStartHour.format('HH:mm'),
                              endHour: employeeEndHour.format('HH:mm'),
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {registration.type === 1 && (
                <div>
                  <div className="form-group">
                    <label className="control-label col-sm-2">
                      <FormattedMessage {...messages.Quantity} />
                    </label>
                    <div className="col-sm-4">
                      <div>
                        <input
                          id="quantityField"
                          type="number"
                          step="1"
                          disabled={invalidResource}
                          value={registration.quantity}
                          onChange={e => this.quantityChanged(e.target.value)}
                          className="form-control"
                        />
                      </div>
                    </div>
                  </div>

                  {countSelectedRelations > 1 &&
                    registration.quantity > 0 &&
                    registration.resource && (
                      <div className="form-group">
                        <div className="col-sm-10 col-sm-offset-2">
                          <FormattedMessage
                            {...messages.RegistrationQuantityPerRelation}
                            values={{
                              quantity: registration.quantity,
                              resource: registration.resource.label,
                            }}
                          />
                        </div>
                      </div>
                    )}
                </div>
              )}
              <div className="form-group">
                {/* BILLABLE */}
                <CanRead on="PriceList">
                  <div className="col-sm-4 col-sm-offset-2">
                    <Checkbox
                      value={registration.isBillable}
                      text={intl.formatMessage(messages.Billable)}
                      disabled={invalidResource}
                      onClick={value =>
                        this.setState({
                          registration: {
                            ...registration,
                            isBillable: value,
                          },
                        })
                      }
                    />
                  </div>
                </CanRead>
                {countSelectedRelations > 1 && (
                  <div className="col-sm-6">
                    <Checkbox
                      value={durationPerRelationActive}
                      text={intl.formatMessage(messages.PerRelationLowerCase)}
                      disabled={invalidResource}
                      onClick={value =>
                        this.setState({
                          durationPerRelationActive: value,
                        })
                      }
                    />
                  </div>
                )}
              </div>
              {/* REMARK */}
              <div className="form-group">
                <label className="control-label col-sm-3">
                  <FormattedMessage {...messages.InvoiceRemark} />
                </label>
                <div className="col-sm-9">
                  <textarea
                    id="RemarkField"
                    className="form-control input-sm vertical-resize"
                    value={registration.remark}
                    disabled={invalidResource}
                    maxLength="3000"
                    onChange={e =>
                      this.setState({
                        registration: {
                          ...registration,
                          remark: e.target.value,
                        },
                      })
                    }
                  />
                </div>
              </div>
              {/* INTERNAL REMARK */}
              <div className="form-group">
                <label className="control-label col-sm-3">
                  <FormattedMessage {...messages.InternalRemark} />
                </label>
                <div className="col-sm-9">
                  <textarea
                    id="InternalRemarkField"
                    className="form-control input-sm vertical-resize"
                    value={registration.internalRemark}
                    disabled={invalidResource}
                    maxLength="3000"
                    onChange={e =>
                      this.setState({
                        registration: {
                          ...registration,
                          internalRemark: e.target.value,
                        },
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </form>
        )}
      </OkCancelModal>
    );
  }
}

export default injectIntl(BatchRegistrationModal);
